// ProjectCard.js - OPRAVENÁ VERZE bez blikání v modal + editace tagů souborů
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
  Edit, 
  Clock, 
  Camera, 
  ChevronDown, 
  ChevronUp, 
  PenTool,
  Trash2,
  Download,
  File, 
  FileText, 
  FileSpreadsheet, 
  Archive,
  Upload,
  Loader,
  ImageIcon,
  Tag // ✅ PŘIDÁNO: Import pro tag ikonu
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import WorkLogList from './WorkLogList';
import { projects, companySettings, fileTags } from '../../services/api';
import FilePreview from './FilePreview';
import { useAuth } from '../../context/AuthContext';
import SignatureModal from './SignatureModal';
import JSZip from 'jszip';
import { getFileTypeInfo } from '../../utils/fileUtils';
import { getEnabledFileTags, getLocalizedLabel } from '../../utils/fileTagsUtils';
import { API_BASE_URL } from '../../services/api.js';
import { downloadFile, downloadAllFiles } from '../../utils/downloadUtils';

const ProjectCard = ({ 
  project, 
  onEdit, 
  onAddWorkLog,
  onEditWorkLog,
  onDeleteWorkLog, 
  onAddFile,
  userRole, 
  currentUserId,
  onProjectUpdate,
  isInModal = false,
  forceLoadFiles = false
}) => {
  const { t, i18n } = useTranslation(); // ✅ OPRAVENO: Přidán i18n
  const { user } = useAuth();
  
  // ✅ STABLE PROJECT ID - avoid re-renders
  const projectId = useMemo(() => {
    if (!project) return null;
    return project.id || project._id;
  }, [project?.id, project?._id]);
  
  // ✅ HELPER FUNKCE pro mapování polí mezi D1 a frontend formáty
  const getProjectField = (field) => {
    if (!project) return '';
    
    const fieldMappings = {
      contractNumber: project.contractNumber || project.contract_number || '',
      startDate: project.startDate || project.start_date || '',
      name: project.name || '',
      description: project.description || '',
      status: project.status || 'Active',
      location: project.location || ''
    };
    
    return fieldMappings[field] || '';
  };
  
  // Basic UI state
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isExpanded, setIsExpanded] = useState(isInModal || forceLoadFiles);
  const [hasExpandedOnce, setHasExpandedOnce] = useState(isInModal || forceLoadFiles);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [selectedFileTag, setSelectedFileTag] = useState('all');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [projectFiles, setProjectFiles] = useState([]); 
  const [fullProjectData, setFullProjectData] = useState(null);
  const [isSavingSignature, setIsSavingSignature] = useState(false);

  // ✅ PŘIDÁNO: Stavy pro editaci tagů souborů
  const [editingFileId, setEditingFileId] = useState(null);
  const [fileEditDropdownOpen, setFileEditDropdownOpen] = useState(false);
  const [updatingFile, setUpdatingFile] = useState(false);
  
  // ✅ R2 Image Support State
  const [imageUrls, setImageUrls] = useState(new Map());
  const [imageLoading, setImageLoading] = useState(new Set());
  const [imageErrors, setImageErrors] = useState(new Set());
  
  // ✅ Signature R2 Support State  
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  
  const contentRef = useRef(null);
  const dropdownRef = useRef(null);
  const fileEditDropdownRef = useRef(null); // ✅ PŘIDÁNO: Ref pro file edit dropdown
  
  // ✅ REFS to track loading states and prevent re-requests
  const loadedImagesRef = useRef(new Set());
  const signatureLoadedRef = useRef(false);

  // ✅ Derived state - safe access to project data
  const files = projectFiles;
  const signature = fullProjectData?.signature || project?.signature;

  // ✅ OPRAVENO: Načítáme z company settings místo file-tags
  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        setIsLoadingSettings(true);

        console.log('📥 Loading company settings for file tags...');
        // ✅ OPRAVA: Načti z company settings kde se ukládají
        const { data } = await companySettings.get();

        if (mounted) {
          console.log('📁 Company settings loaded:', {
            hasFileTags: !!data.fileTags,
            fileTagsCount: data.fileTags?.length || 0,
            fileTags: data.fileTags?.map(tag => ({ 
              code: tag.code, 
              enabled: tag.enabled,
              labels: tag.labels,
              hasMultilingualLabels: !!tag.labels
            }))
          });
          
          setSettings({
            fileTags: data.fileTags || []
          });
        }
      } catch (error) {
        console.error('❌ Error loading company settings:', error);
        
        // ✅ Fallback - zkus file-tags endpoint
        if (mounted) {
          try {
            console.log('🔄 Trying fallback file-tags endpoint...');
            const response = await fileTags.getAll();
            setSettings({ fileTags: response.data || [] });
          } catch (fallbackError) {
            console.error('❌ Fallback also failed:', fallbackError);
            setSettings({ fileTags: [] });
          }
        }
      } finally {
        if (mounted) {
          setIsLoadingSettings(false);
        }
      }
    };

    fetchSettings();

    // ✅ Listen for file tags updates
    const handleFileTagsUpdate = () => {
      console.log('📢 File tags updated in ProjectCard, refreshing...');
      fetchSettings();
    };

    window.addEventListener('fileTagsUpdated', handleFileTagsUpdate);

    return () => {
      mounted = false;
      window.removeEventListener('fileTagsUpdated', handleFileTagsUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ Žádné dependencies - spustí se jen jednou

  // ✅ Watch for changes to the project prop - STABILNÍ
  useEffect(() => {
    if (project && hasExpandedOnce) {
      setFullProjectData(project);
      setProjectFiles(project.files || []);
      console.log('📁 ProjectCard updated files:', project.files?.length || 0);
    }
  }, [project, hasExpandedOnce]);

  // ✅ useRef pro stabilní callback reference
  const onProjectUpdateRef = useRef(onProjectUpdate);
  useEffect(() => {
    onProjectUpdateRef.current = onProjectUpdate;
  }, [onProjectUpdate]);

  // ✅ STABLE callback pro project update - KRITICKÁ OPRAVA
  const handleProjectUpdate = useCallback((updatedProject) => {
    if (onProjectUpdateRef.current && updatedProject) {
      // Pouze aktualizuj pokud se data skutečně změnila
      const currentProject = fullProjectData || project;
      if (JSON.stringify(updatedProject) !== JSON.stringify(currentProject)) {
        console.log('📝 Updating project data:', updatedProject.name);
        onProjectUpdateRef.current(updatedProject);
      }
    }
  }, []); // ✅ Žádné dependencies - plně stabilní

  // ✅ FIXED fetchFullProject - STABLE dependencies BEZ onProjectUpdate
  const fetchFullProject = useCallback(async () => {
    if (!projectId) {
      console.warn('No project ID available for fetchFullProject');
      return;
    }

    // ✅ Zabran opakovanému načítání pokud už načítáme
    if (isLoadingFiles) {
      console.log('🔄 Already loading files, skipping...');
      return;
    }

    try {
      setIsLoadingFiles(true);
      console.log('📥 Fetching full project data for:', projectId);
      const { data } = await projects.getById(projectId);
      setFullProjectData(data);
      setProjectFiles(data.files || []);
      console.log('📁 fetchFullProject loaded files:', data.files?.length || 0);
      
      // ✅ KRITICKÁ OPRAVA: Použij stabilní callback
      handleProjectUpdate(data);
    } catch (error) {
      console.error('Error loading full project data:', error);
    } finally {
      setIsLoadingFiles(false);
    }
  }, [projectId, isLoadingFiles, handleProjectUpdate]); // ✅ STABLE dependencies

  // ✅ FIXED: Vráceno do jednoho useEffect ale bez fetchFullProject dependency
  useEffect(() => {
    if ((isExpanded && !hasExpandedOnce) || (isInModal && !hasExpandedOnce) || forceLoadFiles) {
      console.log('🚀 Loading project data - expanded:', isExpanded, 'inModal:', isInModal, 'forceLoad:', forceLoadFiles);
      fetchFullProject();
      setHasExpandedOnce(true);
    }
  }, [isExpanded, hasExpandedOnce, isInModal, forceLoadFiles]); // ✅ fetchFullProject ODSTRANĚNO, ale všechny ostatní zachovány

  // ✅ COMPLETELY FIXED loadImageFromAPI - NO re-requests
  const loadImageFromAPI = useCallback(async (file) => {
    if (!file || !projectId) return;
    
    const fileId = file.id || file._id;
    if (!fileId) return;
    
    // ✅ CRITICAL: Check if already loaded or loading
    if (loadedImagesRef.current.has(fileId) || 
        imageUrls.has(fileId) || 
        imageLoading.has(fileId)) {
      return; // Already processed
    }
    
    // Mark as loading
    loadedImagesRef.current.add(fileId);
    setImageLoading(prev => new Set(prev).add(fileId));
    
    console.log('🖼️ Loading image from API for file:', fileId);

    try {
      // If file has base64, use it (backward compatibility)
      if (file.base64) {
        console.log('✅ Using base64 data for file:', fileId);
        setImageUrls(prev => new Map(prev).set(fileId, file.base64));
        return;
      }

      // Otherwise load from R2 via API
      console.log('📥 Loading from R2 for file:', fileId);
      const token = localStorage.getItem('token');
      const apiBase = API_BASE_URL;
      
      const response = await fetch(`${apiBase}/api/projects/${projectId}/file/${fileId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setImageUrls(prev => new Map(prev).set(fileId, imageUrl));
        console.log('✅ Image loaded from R2 for file:', fileId, 'Size:', blob.size);
      } else {
        throw new Error(`Failed to load: ${response.status} ${response.statusText}`);
      }

    } catch (error) {
      console.error(`❌ Failed to load image ${fileId}:`, error);
      setImageErrors(prev => new Set(prev).add(fileId));
      loadedImagesRef.current.delete(fileId); // Allow retry
    } finally {
      setImageLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  }, [projectId]); // ✅ STABLE dependency

  // ✅ FIXED Load signature from R2 - NO re-requests
  const loadSignatureFromAPI = useCallback(async () => {
    if (!signature || !projectId) return;

    // ✅ CRITICAL: Check if already loaded using ref (not state)
    if (signatureLoadedRef.current) {
      return; // Already processed
    }

    // Reset states
    setSignatureError(false);

    // If signature has base64, use it directly (backward compatibility)
    if (signature.base64) {
      console.log('📜 Using base64 signature data');
      setSignatureUrl(signature.base64);
      signatureLoadedRef.current = true;
      return;
    }

    // If no R2 key, no signature to load
    if (!signature.r2_key && !signature.download_url) {
      console.log('⚠️ No signature R2 key or download URL found');
      return;
    }

    console.log('🖼️ Loading signature from R2 for project:', projectId);
    signatureLoadedRef.current = true; // Mark as processed
    setSignatureLoading(true);

    try {
      const token = localStorage.getItem('token');
      const apiBase = API_BASE_URL;

      // ✅ OPTIMALIZACE: Backend sends no-cache headers, no need for cache-busting
      const response = await fetch(`${apiBase}/api/projects/${projectId}/signature/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
        // No cache: 'no-store' needed - backend handles it
      });

      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setSignatureUrl(imageUrl);
        console.log('✅ Signature loaded from R2, Size:', blob.size);
      } else {
        throw new Error(`Failed to load signature: ${response.status} ${response.statusText}`);
      }

    } catch (error) {
      console.error(`❌ Failed to load signature:`, error);
      setSignatureError(true);
      signatureLoadedRef.current = false; // Allow retry
    } finally {
      setSignatureLoading(false);
    }
  }, [signature, projectId]); // ✅ OPRAVENO: Odstraněny signatureLoading a signatureUrl z dependencies

  // ✅ FIXED Auto-load images when expanded - CONTROL re-runs
  useEffect(() => {
    if ((isExpanded || isInModal) && files.length > 0) {
      const imageFiles = files.filter(file => 
        (file.fileType || file.file_type || '').startsWith('image/') || !file.fileType
      );
      
      console.log(`🖼️ Auto-loading ${imageFiles.length} images for project ${project?.name}`);
      
      // ✅ Load only NEW images
      imageFiles.forEach(file => {
        const fileId = file.id || file._id;
        if (!loadedImagesRef.current.has(fileId)) {
          loadImageFromAPI(file);
        }
      });
    }
  }, [isExpanded, isInModal, files, project?.name, loadImageFromAPI]);

  // ✅ FIXED Auto-load signature when available - CONTROL re-runs
  useEffect(() => {
    if (signature && (isExpanded || isInModal) && !signatureLoadedRef.current) {
      loadSignatureFromAPI();
    }
  }, [signature, isExpanded, isInModal, loadSignatureFromAPI]);

  // ✅ RESET tracking when project changes
  useEffect(() => {
    if (projectId) {
      // Reset tracking když se změní projekt
      setHasExpandedOnce(isInModal || forceLoadFiles);
      setFullProjectData(null);
      setProjectFiles([]);
      
      // Clear image cache
      loadedImagesRef.current.clear();
      signatureLoadedRef.current = false;
      setImageUrls(new Map());
      setImageLoading(new Set());
      setImageErrors(new Set());
      setSignatureUrl(null);
      setSignatureLoading(false);
      setSignatureError(false);
      
      // ✅ PŘIDÁNO: Reset file edit states
      setEditingFileId(null);
      setFileEditDropdownOpen(false);
      setUpdatingFile(false);
      
      console.log('🔄 Reset project card state for:', projectId);
    }
  }, [projectId, isInModal, forceLoadFiles]);

  // ✅ Cleanup blob URLs when project changes
  useEffect(() => {
    return () => {
      imageUrls.forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      
      if (signatureUrl && signatureUrl.startsWith('blob:')) {
        URL.revokeObjectURL(signatureUrl);
      }
    };
  }, [projectId]);

  // Get file tags from settings or use defaults
  const getFileTags = () => {
    if (settings && settings.fileTags) {
      return getEnabledFileTags(settings);
    }
    
    return [
      { 
        code: 'all', 
        labels: { en: 'All Files', cs: 'Všechny soubory' },
        enabled: true 
      },
      { 
        code: 'document', 
        labels: { en: 'Documents', cs: 'Dokumenty' },
        enabled: true 
      },
      { 
        code: 'inspection', 
        labels: { en: 'Inspection', cs: 'Inspekce' },
        enabled: true 
      },
      { 
        code: 'implementation', 
        labels: { en: 'Implementation', cs: 'Realizace' },
        enabled: true 
      },
      { 
        code: 'contract', 
        labels: { en: 'Contracts', cs: 'Smlouvy' },
        enabled: true 
      }
    ];
  };
  
  // ✅ OPRAVENO: getSelectedTagLabel s explicitním jazykem a useCallback
  const getSelectedTagLabel = useCallback(() => {
    const tags = getFileTags();
    const tag = tags.find(tag => tag.code === selectedFileTag);
    
    // DEBUG log
    console.log('🔍 getSelectedTagLabel:', {
      currentLanguage: i18n.language,
      selectedFileTag,
      foundTag: tag,
      allTags: tags
    });
    
    return tag ? getLocalizedLabel(tag, i18n.language) : t('files.allFiles', 'All Files');
  }, [selectedFileTag, settings, i18n.language, t]);

  // ✅ NOVÉ: useEffect pro sledování změny jazyka
  useEffect(() => {
    // Force re-render when language changes to update file tags labels
    console.log('🌐 Language changed to:', i18n.language);
  }, [i18n.language]);

  // ✅ PŘIDÁNO: useEffect pro zavření file edit dropdown při kliknutí mimo
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fileEditDropdownRef.current && !fileEditDropdownRef.current.contains(event.target)) {
        setFileEditDropdownOpen(false);
        setEditingFileId(null);
      }
    };

    if (fileEditDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [fileEditDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Permission checks
  const isAssigned = project?.assignedTechnicians?.some(
    tech => tech?._id === currentUserId
  ) || false;

  const isAdminUser = userRole === 'admin' || userRole === 'officeAdmin';

  const canDeleteFile = (file) => {
    const isAdmin = userRole === 'admin' || userRole === 'officeAdmin';
    const isUploader = file.uploadedBy?._id === currentUserId;
    return isAdmin || isUploader;
  };

  // ✅ PŘIDÁNO: Permission check pro editaci souborů
  const canEditFile = (file) => {
    const isAdmin = userRole === 'admin' || userRole === 'officeAdmin';
    const isUploader = file.uploadedBy?._id === currentUserId;
    return isAdmin || isUploader;
  };

  // ✅ PŘIDÁNO: Helper funkce pro barvy tagů
  const getTagColor = (tagCode) => {
    const colors = {
      'document': 'bg-blue-500',
      'contract': 'bg-purple-500', 
      'technical': 'bg-green-500',
      'inspection': 'bg-orange-500',
      'implementation': 'bg-indigo-500',
      'invoice': 'bg-yellow-500',
      'photo': 'bg-pink-500'
    };
    return colors[tagCode] || 'bg-gray-500';
  };

  // ✅ PŘIDÁNO: Handler pro aktualizaci file tagu
  const handleUpdateFileTag = async (fileId, newTag) => {
    try {
      setUpdatingFile(true);
      
      const { data } = await projects.updateFile(projectId, fileId, { tag: newTag });
      
      // Update local state
      setProjectFiles(data.files || []);
      
      // Refresh project
      await fetchFullProject();
      
      if (onProjectUpdate) {
        onProjectUpdate(data);
      }
      
      setEditingFileId(null);
      setFileEditDropdownOpen(false);
      
    } catch (error) {
      console.error('Error updating file tag:', error);
      alert('Failed to update file tag: ' + (error.response?.data?.message || error.message));
    } finally {
      setUpdatingFile(false);
    }
  };

  // Height calculation for expansion
  useEffect(() => {
    let unmounted = false;

    const checkHeight = () => {
      if (contentRef.current && !unmounted) {
        requestAnimationFrame(() => {
          const scrollHeight = contentRef.current.scrollHeight;
          if (!unmounted) {
            const needsExpand = !isInModal && scrollHeight > 160;
            setNeedsExpansion(needsExpand);
          }
        });
      }
    };

    checkHeight();
    const timer = setTimeout(checkHeight, 100);
    window.addEventListener('resize', checkHeight);
    
    return () => {
      unmounted = true;
      clearTimeout(timer);
      window.removeEventListener('resize', checkHeight);
    };
  }, [project, isInModal]);

  // File handlers
  const handleDeleteFile = async (fileId) => {
    if (!window.confirm(t('files.deleteFile', 'Are you sure you want to delete this file?'))) {
      return;
    }
  
    try {
      setIsDeleting(true);
      console.log('Deleting file:', { projectId, fileId });
      
      const { data } = await projects.deleteFile(projectId, fileId);
      
      // Update local state
      setProjectFiles(prevFiles => prevFiles.filter(file => (file.id || file._id) !== fileId));
      
      // Cleanup image URL if it exists
      setImageUrls(prev => {
        const newMap = new Map(prev);
        const url = newMap.get(fileId);
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
        newMap.delete(fileId);
        return newMap;
      });

      // Remove from error state and tracking
      setImageErrors(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
      
      loadedImagesRef.current.delete(fileId);
      
      await fetchFullProject();
      
      if (onProjectUpdate) {
        onProjectUpdate(data);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      
      let errorMessage = t('files.errors.deleteFailed', 'Failed to delete file: ');
      
      if (error.response) {
        errorMessage += error.response.data?.message || 
                      `Server error: ${error.response.status}`;
        
        if (error.response.status === 404) {
          errorMessage = 'The file could not be found. It may have been deleted already.';
        }
      } else if (error.request) {
        errorMessage += t('files.errors.noResponse', 'No response from server');
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFileClick = (file) => {
    // Handle non-image files differently depending on type
    if (file.fileType?.startsWith('image/') ||
        file.fileType === 'application/pdf' ||
        file.fileType === 'text/plain') {
      // Preview these file types
      // ✅ OPTIMALIZACE: Pass already loaded blob URL to prevent re-download
      const fileId = file.id || file._id;
      const preloadedUrl = imageUrls.get(fileId);
      setSelectedFile({
        ...file,
        preloadedBlobUrl: preloadedUrl // Add blob URL if available
      });
    } else {
      // Download other file types directly using downloadUtils
      downloadFile(file, projectId);
    }
  };

  // ✅ OPRAVENÁ FUNKCE: Použití downloadUtils.js místo vlastní implementace
  const handleDownloadAllFiles = async () => {
    try {
      if (files.length === 0) {
        alert('No files to download');
        return;
      }

      const availableTags = getFileTags();
      
      // ✅ Volání funkce z downloadUtils.js s podporou kategorií
      await downloadAllFiles(files, projectId, {
        organizeByCategory: true,  // Organizace do složek podle tagů
        categoryLabels: availableTags,  // Mapping tagů na názvy složek
        zipFileName: `project_${getProjectField('contractNumber') || getProjectField('name') || projectId}_files.zip`
      });
      
    } catch (error) {
      console.error('Error downloading files:', error);
      alert(`Error downloading files: ${error.message}`);
    }
  };

  // ✅ UPDATED: Signature save handler for R2
  const handleSignatureSave = async (signatureData) => {
    try {
      setIsSavingSignature(true);
      const response = await projects.addSignature(projectId, signatureData);
      
      if (response && response.data) {
        if (fullProjectData) {
          setFullProjectData({
            ...fullProjectData,
            signature: response.data.signature
          });
        }
        
        // ✅ Clear signature cache for reload
        if (signatureUrl && signatureUrl.startsWith('blob:')) {
          URL.revokeObjectURL(signatureUrl);
        }
        setSignatureUrl(null);
        setSignatureLoading(false);
        setSignatureError(false);
        signatureLoadedRef.current = false; // Reset for reload
        
        if (onProjectUpdate) {
          onProjectUpdate(response.data);
        }
      }
      
      await fetchFullProject();
      
      return response;
    } catch (error) {
      console.error('Error saving signature:', error);
      throw error;
    } finally {
      setIsSavingSignature(false);
    }
  };

  // Utility functions
  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800',
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-blue-100 text-blue-800',
      'OnHold': 'bg-orange-100 text-orange-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Filter files by selected tag
  const filteredFiles = files.filter(file => 
    selectedFileTag === 'all' ? true : file.tag === selectedFileTag
  );

  if (!project) {
    return null;
  }

  return (
    <>
      <div className={`bg-white ${!isInModal ? 'overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-200' : ''}`}>
        <div className="p-4">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 break-words">
                {getProjectField('name') || t('projects.unnamed')}
              </h3>
              <p className="text-sm text-gray-500">#{getProjectField('contractNumber')}</p>
            </div>
            <div className="flex justify-end space-x-2">
              {(isAssigned || isAdminUser) && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddFile(projectId);
                    }}
                    className="p-2 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50"
                    title={t('files.uploadFiles')}
                  >
                    <Upload className="h-5 w-5" />
                  </button>
                  {userRole === 'technician' && isAssigned && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddWorkLog(projectId);
                      }}
                      className="p-2 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50"
                      title={t('workLogs.logHours')}
                    >
                      <Clock className="h-5 w-5" />
                    </button>
                  )}
                </>
              )}
              {isAdminUser && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(project);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-500"
                  title={t('common.actions.edit')}
                >
                  <Edit className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
  
          {/* Project Details Section */}
          <div className="relative">
            <div
              ref={contentRef}
              className={`space-y-4 transition-all duration-200 ${
                !isExpanded && !isInModal ? 'max-h-[160px] overflow-hidden' : ''
              }`}
            >
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-600">
                    {t('projects.startDate')}
                  </h4>
                  <p className="text-sm text-gray-900">{formatDate(getProjectField('startDate'))}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-600">
                    {t('projects.status')}
                  </h4>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(getProjectField('status'))}`}>
                    {getProjectField('status') || t('projects.statusUnknown')}
                  </span>
                </div>
              </div>
  
              {/* Technicians */}
              {project.assignedTechnicians?.length > 0 && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-gray-600">
                    {t('projects.assignedTechnicians')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {project.assignedTechnicians
                      .filter(Boolean)
                      .map((tech, index) => (
                        <span 
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tech.name}
                          {tech.companyRole && (
                            <span className="ml-1">- {tech.companyRole.name}</span>
                          )}
                        </span>
                      ))}
                  </div>
                </div>
              )}
  
              {/* Description */}
              {getProjectField('description') && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-gray-600">
                    {t('projects.description')}
                  </h4>
                  <p className="text-sm text-gray-700 break-words whitespace-pre-wrap">
                    {getProjectField('description')}
                  </p>
                </div>
              )}
  
              {/* ✅ UPDATED: Signature Section with R2 Support */}
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium text-gray-600">
                    {t('projects.customerSignature')}
                  </h4>
                  {(isAssigned || isAdminUser) && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsSignatureModalOpen(true);
                      }}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      <PenTool className="h-4 w-4 mr-1" />
                      {signature ? t('projects.updateSignature') : t('projects.addSignature')}
                    </button>
                  )}
                </div>
                
                {signature ? (
                  <div className="bg-gray-50 p-3 rounded">
                    {/* ✅ Signature Loading with states */}
                    {signatureLoading && (
                      <div className="flex items-center justify-center h-24">
                        <div className="flex flex-col items-center space-y-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                          <p className="text-xs text-gray-500">Loading signature...</p>
                        </div>
                      </div>
                    )}
                    
                    {signatureError && !signatureLoading && (
                      <div className="flex items-center justify-center h-24 bg-gray-100 rounded border-2 border-dashed border-gray-300">
                        <div className="text-center">
                          <PenTool className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                          <p className="text-xs text-gray-500">Cannot load signature</p>
                        </div>
                      </div>
                    )}
                    
                    {signatureUrl && !signatureLoading && !signatureError && (
                      <img
                        src={signatureUrl}
                        alt={t('projects.signatureImage')}
                        className="max-h-24 mx-auto cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile({
                            isSignature: true,
                            signature: signature,
                            projectId: projectId,
                            preloadedBlobUrl: signatureUrl // ✅ OPTIMALIZACE: Pass already loaded signature
                          });
                        }}
                        onError={() => {
                          console.error('❌ Signature display error');
                          setSignatureError(true);
                        }}
                      />
                    )}
                    
                    <p className="text-xs text-gray-500 text-center mt-2">
                      {t('projects.signedOn', { date: new Date(signature.date).toLocaleDateString() })}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">{t('projects.noSignature')}</p>
                )}
              </div>
  
              {/* Work Logs */}
              {project.workLogs?.length > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-medium text-gray-600">
                      {t('workLogs.title')}
                    </h4>
                    <span className="text-sm font-medium text-gray-600">
                      {project.workLogs.reduce((sum, log) => sum + (log.hours || 0), 0).toFixed(1)} hrs
                    </span>
                  </div>
                  <WorkLogList
                    workLogs={project.workLogs}
                    projectId={projectId}
                    currentUserId={currentUserId}
                    onEdit={onEditWorkLog}
                    onDelete={onDeleteWorkLog}
                    userRole={userRole}
                  />
                </div>
              )}
  
              {/* ✅ OPRAVENO: Files & Photos Section s překladem */}
              {(isExpanded || isInModal) && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-medium text-gray-600">
                      {t('files.filesAndPhotos', 'Files & Photos')}
                    </h4>
                    {files.length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadAllFiles();
                        }}
                        className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
                        title={t('files.downloadAll')}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        {t('files.downloadAll', 'Download All')}
                      </button>
                    )}
                  </div>
                  
                  {/* Loading State */}
                  {isLoadingFiles ? (
                    <div className="flex items-center justify-center h-40 bg-gray-50 rounded-lg">
                      <div className="flex flex-col items-center space-y-2">
                        <Loader className="h-6 w-6 text-blue-500 animate-spin" />
                        <p className="text-sm text-gray-500">{t('common.messages.loading', 'Loading files...')}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Files Filter Dropdown */}
                      {files.length > 0 && (
                        <div className="flex flex-col gap-2">
                          <div className="relative" ref={dropdownRef}>
                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsDropdownOpen(!isDropdownOpen);
                              }} 
                              className="flex items-center justify-between cursor-pointer p-2 border rounded-md bg-white shadow-sm hover:bg-gray-50"
                            >
                              <span className="text-sm font-medium">
                                {isLoadingSettings ? t('common.messages.loading', 'Loading...') : getSelectedTagLabel()}
                              </span>
                              <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                            </div>
                            
                            {/* ✅ OPRAVENÝ dropdown - absolute místo fixed */}
                            {isDropdownOpen && (
                              <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg border py-1 max-h-60 overflow-y-auto">
                                {getFileTags().map(tag => (
                                  <button
                                    key={tag.code}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedFileTag(tag.code);
                                      setIsDropdownOpen(false);
                                    }}
                                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                                      selectedFileTag === tag.code ? 'bg-blue-50 text-blue-700 font-medium' : ''
                                    }`}
                                  >
                                    {getLocalizedLabel(tag)}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                  
                          {/* Files Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {filteredFiles.map((file, index) => {
                              const fileInfo = getFileTypeInfo(file.fileType || 'application/octet-stream');
                              const fileId = file.id || file._id;
                              
                              // ✅ Image rendering with R2 support + TAG EDITING
                              if (file.fileType?.startsWith('image/') || !file.fileType) {
                                return (
                                  <div 
                                    key={index} 
                                    className="group relative aspect-square cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedFile(file);
                                    }}
                                  >
                                    {/* ✅ PŘIDÁNO: File tag overlay - jen pro admin/autora */}
                                    {canEditFile(file) && (
                                      <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                        <div className="relative" ref={editingFileId === fileId ? fileEditDropdownRef : null}>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingFileId(fileId);
                                              setFileEditDropdownOpen(true);
                                            }}
                                            disabled={updatingFile}
                                            className={`px-2 py-1 text-xs rounded-full text-white font-medium ${
                                              getTagColor(file.tag || 'document')
                                            } hover:opacity-80 transition-opacity`}
                                            title="Click to change category"
                                          >
                                            {getLocalizedLabel(getFileTags().find(tag => tag.code === (file.tag || 'document')))}
                                          </button>
                                          
                                          {/* Tag edit dropdown */}
                                          {editingFileId === fileId && fileEditDropdownOpen && (
                                            <div className="absolute z-50 mt-1 w-32 bg-white rounded-md shadow-lg border py-1 max-h-48 overflow-y-auto">
                                              {getFileTags()
                                                .filter(tag => tag.code !== 'all')
                                                .map(tag => (
                                                  <button
                                                    key={tag.code}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleUpdateFileTag(fileId, tag.code);
                                                    }}
                                                    disabled={updatingFile}
                                                    className={`w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100 transition-colors ${
                                                      (file.tag || 'document') === tag.code ? 'bg-blue-50 text-blue-700 font-medium' : ''
                                                    } disabled:opacity-50`}
                                                  >
                                                    {getLocalizedLabel(tag)}
                                                  </button>
                                                ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Loading state */}
                                    {imageLoading.has(fileId) && !imageUrls.has(fileId) && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                                        <div className="flex flex-col items-center space-y-1">
                                          <Loader className="h-4 w-4 text-blue-500 animate-spin" />
                                          <span className="text-xs text-gray-500">Loading...</span>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Error state */}
                                    {imageErrors.has(fileId) && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                                        <div className="flex flex-col items-center space-y-1">
                                          <ImageIcon className="h-8 w-8 text-gray-400" />
                                          <span className="text-xs text-gray-500 text-center px-1">Failed to load</span>
                                          <button 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setImageErrors(prev => {
                                                const newSet = new Set(prev);
                                                newSet.delete(fileId);
                                                return newSet;
                                              });
                                              loadedImagesRef.current.delete(fileId);
                                              loadImageFromAPI(file);
                                            }}
                                            className="text-xs text-blue-600 hover:text-blue-800"
                                          >
                                            Retry
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Successful image */}
                                    {imageUrls.has(fileId) && (
                                      <img
                                        src={imageUrls.get(fileId)}
                                        alt={file.originalName || file.file_name || 'Project image'}
                                        className="w-full h-full object-cover rounded-lg"
                                        onError={(e) => {
                                          console.error('Image display error for file:', fileId);
                                          setImageErrors(prev => new Set(prev).add(fileId));
                                        }}
                                      />
                                    )}
                                    
                                    {/* File info overlay */}
                                    <div className="absolute inset-x-0 bottom-0 bg-black bg-opacity-60 text-white p-2 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                      <p className="text-xs truncate">
                                        {file.originalName || file.file_name || 'Unknown'}
                                      </p>
                                    </div>
                                    
                                    {/* Delete button */}
                                    {canDeleteFile(file) && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteFile(fileId);
                                        }}
                                        disabled={isDeleting}
                                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 disabled:opacity-50"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                );
                              } else {
                                // ✅ Renderování ostatních typů souborů (PDF, DOC, atd.) + TAG EDITING
                                return (
                                  <div 
                                    key={index} 
                                    className="group relative aspect-square cursor-pointer border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFileClick(file);
                                    }}
                                  >
                                    {/* File icon based on type */}
                                    <div className="flex flex-col items-center justify-center h-full p-3">
                                      {file.fileType === 'application/pdf' && (
                                        <FileText className="h-12 w-12 text-red-500 mb-2" />
                                      )}
                                      {file.fileType?.includes('word') && (
                                        <FileText className="h-12 w-12 text-blue-500 mb-2" />
                                      )}
                                      {file.fileType?.includes('sheet') && (
                                        <FileSpreadsheet className="h-12 w-12 text-green-500 mb-2" />
                                      )}
                                      {file.fileType?.startsWith('text/') && (
                                        <FileText className="h-12 w-12 text-gray-500 mb-2" />
                                      )}
                                      {!file.fileType?.startsWith('text/') && 
                                       !file.fileType?.includes('word') && 
                                       !file.fileType?.includes('sheet') && 
                                       file.fileType !== 'application/pdf' && (
                                        <File className="h-12 w-12 text-gray-500 mb-2" />
                                      )}
                                      
                                      {/* File name and info */}
                                      <p className="text-xs text-center font-medium text-gray-700 truncate w-full">
                                        {file.originalName || file.file_name || 'Unknown'}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {fileInfo.displayType}
                                      </p>
                                      {file.size && (
                                        <p className="text-xs text-gray-400">
                                          {(file.size / 1024).toFixed(1)} KB
                                        </p>
                                      )}

                                      {/* ✅ PŘIDÁNO: File tag badge - pro admin/autora editovatelný */}
                                      <div className="mt-2">
                                        {canEditFile(file) ? (
                                          <div className="relative" ref={editingFileId === fileId ? fileEditDropdownRef : null}>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingFileId(fileId);
                                                setFileEditDropdownOpen(true);
                                              }}
                                              disabled={updatingFile}
                                              className={`px-2 py-1 text-xs rounded-full text-white font-medium ${
                                                getTagColor(file.tag || 'document')
                                              } hover:opacity-80 transition-opacity`}
                                              title="Click to change category"
                                            >
                                              {getLocalizedLabel(getFileTags().find(tag => tag.code === (file.tag || 'document')))}
                                            </button>
                                            
                                            {editingFileId === fileId && fileEditDropdownOpen && (
                                              <div className="absolute z-50 mt-1 w-32 bg-white rounded-md shadow-lg border py-1 max-h-48 overflow-y-auto left-1/2 transform -translate-x-1/2">
                                                {getFileTags()
                                                  .filter(tag => tag.code !== 'all')
                                                  .map(tag => (
                                                    <button
                                                      key={tag.code}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUpdateFileTag(fileId, tag.code);
                                                      }}
                                                      disabled={updatingFile}
                                                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100 transition-colors ${
                                                        (file.tag || 'document') === tag.code ? 'bg-blue-50 text-blue-700 font-medium' : ''
                                                      } disabled:opacity-50`}
                                                    >
                                                      {getLocalizedLabel(tag)}
                                                    </button>
                                                  ))}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <span className={`px-2 py-1 text-xs rounded-full text-white font-medium ${
                                            getTagColor(file.tag || 'document')
                                          }`}>
                                            {getLocalizedLabel(getFileTags().find(tag => tag.code === (file.tag || 'document')))}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Download indicator */}
                                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      {file.fileType === 'application/pdf' || file.fileType?.startsWith('text/') ? (
                                        <div className="bg-blue-500 text-white p-1 rounded text-xs">
                                          Preview
                                        </div>
                                      ) : (
                                        <div className="bg-green-500 text-white p-1 rounded text-xs">
                                          Download
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Delete button */}
                                    {canDeleteFile(file) && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteFile(fileId);
                                        }}
                                        disabled={isDeleting}
                                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 disabled:opacity-50"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                );
                              }
                            })}
                          </div>
                  
                          {filteredFiles.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-4">
                              {t('files.noFilesForTag', 'No files found for selected category')}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {files.length === 0 && (
                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-500">{t('files.noFiles', 'No files uploaded yet')}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
  
            {/* Gradient Overlay */}
            {needsExpansion && !isExpanded && !isInModal && (
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white via-white to-transparent" />
            )}
          </div>
  
          {/* Show More/Less Button - don't show in modal */}
          {needsExpansion && !isInModal && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="w-full py-2 px-4 flex items-center justify-center text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors border-t"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  {t('common.showLess')}
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  {t('common.showMore')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
  
      {/* File Preview Modal with signature support */}
      <FilePreview
        isOpen={selectedFile !== null}
        onClose={() => setSelectedFile(null)}
        file={selectedFile?.isSignature ? null : selectedFile}
        signature={selectedFile?.isSignature ? selectedFile.signature : null}
        projectId={selectedFile?.projectId || projectId}
      />
  
      {/* Signature Modal */}
      {isSignatureModalOpen && (
        <SignatureModal
          isOpen={isSignatureModalOpen}
          onClose={() => setIsSignatureModalOpen(false)}
          onSave={handleSignatureSave}
          title={t('projects.signatureFor', { name: getProjectField('name') || 'Project' })}
        />
      )}
    </>
  );
};

export default ProjectCard;